import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import type { User, Session } from '@supabase/supabase-js';
import { supabase, SubscriptionStatus, hasFullAccess as checkFullAccess, isSupabaseConfigured } from '@/lib/supabase';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: any | null;
  subscriptionStatus: SubscriptionStatus;
  hasFullAccess: boolean;
  mustChangePassword: boolean;
  isLoading: boolean;
  signInWithOtp: (email: string) => Promise<{ error: Error | null }>;
  verifyOtp: (email: string, token: string) => Promise<{ error: Error | null }>;
  signInWithPassword: (email: string, password: string) => Promise<{ error: Error | null }>;
  updatePasswordAndClearFlag: (newPassword: string) => Promise<{ error: Error | null }>;
  refreshProfile: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<any | null>(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus>('free');
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!isSupabaseConfigured) {
      setIsLoading(false);
      return;
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        setIsLoading(true);
        fetchProfile(session.user.id, session.user.email);
      } else {
        setIsLoading(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        setIsLoading(true);
        fetchProfile(session.user.id, session.user.email);
      } else {
        setProfile(null);
        setSubscriptionStatus('free');
        setMustChangePassword(false);
        setIsLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchProfile = async (userId: string, userEmail?: string) => {
    try {
      let profileData = null;

      // Step 1: Try RPC function with SECURITY DEFINER (same as mobile app)
      if (userEmail) {
        const { data: rpcData, error: rpcError } = await supabase.rpc('find_profile_by_email', {
          p_email: userEmail
        });
        if (rpcData && !rpcError) {
          profileData = rpcData;
        }
      }

      // Step 2: Fall back to direct query by email
      if (!profileData && userEmail) {
        const { data: emailData, error: emailError } = await supabase
          .from('profiles')
          .select('*')
          .eq('email', userEmail)
          .maybeSingle();
        if (emailData && !emailError) {
          profileData = emailData;
        }
      }

      // Step 3: Fall back to direct query by user_id
      if (!profileData) {
        const { data: idData, error: idError } = await supabase
          .from('profiles')
          .select('*')
          .eq('user_id', userId)
          .maybeSingle();
        if (idData && !idError) {
          profileData = idData;
        }
      }

      if (profileData) {
        setProfile(profileData);
        setSubscriptionStatus(profileData.subscription_status || 'free');
        setMustChangePassword(Boolean(profileData.must_change_password));
      } else {
        setProfile(null);
        setSubscriptionStatus('free');
        setMustChangePassword(false);
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
      setProfile(null);
      setSubscriptionStatus('free');
      setMustChangePassword(false);
    } finally {
      setIsLoading(false);
    }
  };

  const signInWithOtp = async (email: string) => {
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: true,
        },
      });
      return { error: error ? new Error(error.message) : null };
    } catch (err) {
      return { error: err as Error };
    }
  };

  const verifyOtp = async (email: string, token: string) => {
    try {
      const { error } = await supabase.auth.verifyOtp({
        email,
        token,
        type: 'email',
      });
      return { error: error ? new Error(error.message) : null };
    } catch (err) {
      return { error: err as Error };
    }
  };

  const signInWithPassword = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      return { error: error ? new Error(error.message) : null };
    } catch (err) {
      return { error: err as Error };
    }
  };

  const updatePasswordAndClearFlag = async (newPassword: string) => {
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password: newPassword });
      if (updateError) {
        return { error: new Error(updateError.message) };
      }
      const { error: rpcError } = await supabase.rpc('clear_must_change_password');
      if (rpcError) {
        console.error('clear_must_change_password RPC failed:', rpcError);
        return {
          error: new Error(
            'Password was updated, but we could not clear the password-change requirement. Please try again or contact support.',
          ),
        };
      }
      if (user) {
        await fetchProfile(user.id, user.email);
      } else {
        setMustChangePassword(false);
      }
      return { error: null };
    } catch (err) {
      return { error: err as Error };
    }
  };

  const refreshProfile = async () => {
    if (user) {
      await fetchProfile(user.id, user.email);
    }
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
      setUser(null);
      setSession(null);
      setProfile(null);
      setSubscriptionStatus('free');
      setMustChangePassword(false);
    } catch (err) {
      console.error('Error signing out:', err);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile,
        subscriptionStatus,
        hasFullAccess: checkFullAccess(subscriptionStatus),
        mustChangePassword,
        isLoading,
        signInWithOtp,
        verifyOtp,
        signInWithPassword,
        updatePasswordAndClearFlag,
        refreshProfile,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
